class SliderComponent extends HTMLElement {
  constructor() {
    const debug = false;
    super();
    if(debug) {
      console.log("SliderComponent initialized");
    }

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if(debug) {
      console.log("Slider found:", this.slider);
      console.log("Previous button found:", this.prevButton);
      console.log("Next button found:", this.nextButton);
    }

    // Bail out when the markup is incomplete (previously only checked when debug was on).
    if (!this.slider || !this.prevButton || !this.nextButton) {
      if(debug) { console.warn("Slider or buttons not found. Exiting constructor."); }
      return;
    }

    // A horizontally scrolling region must be reachable by keyboard (axe: scrollable-region-focusable).
    if (!this.slider.hasAttribute('tabindex')) this.slider.setAttribute('tabindex', '0');

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => {
      if(debug) { console.log("Resize observed. Reinitializing pages."); }
      this.initPages();
    });
    resizeObserver.observe(this.slider);

    // Below the desktop breakpoint the slider is only as tall as the slide(s) in view (see fitHeight()). Slides can change
    // height after load (lazy images, wrapping text), so re-fit when any of them resizes.
    this.compactQuery = window.matchMedia('(max-width: 989px)');
    this.compactQuery.addEventListener('change', () => this.fitHeight());
    if (!this.enableSliderLooping) {
      const slideObserver = new ResizeObserver(() => this.fitHeight());
      this.sliderItems.forEach((item) => slideObserver.observe(item));
    }

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
    if(debug) { console.log("Event listeners added to buttons."); }
  }

  initPages() {
    const debug = false;
    if(debug) { console.log("Initializing pages..."); }
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );

    if(debug) { console.log("Visible slides:", this.sliderItemsToShow.length); }

    // Desktop end gutter (component-slider.css): only for a slider whose slides overflow it. Measured from the slides
    // themselves, not scrollWidth, which the gutter would inflate.
    const lastItem = this.sliderItemsToShow[this.sliderItemsToShow.length - 1];
    if (lastItem) {
      this.slider.classList.toggle('slider--end-gutter', lastItem.offsetLeft + lastItem.offsetWidth > this.slider.clientWidth + 1);
    }

    if (this.sliderItemsToShow.length < 2) {
      if(debug) { console.warn("Not enough slides to paginate."); }
      return;
    }

    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    if(debug) { console.log("Slide offset:", this.sliderItemOffset); }

    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

    if(debug) {
      console.log("Slides per page:", this.slidesPerPage);
      console.log("Total pages:", this.totalPages);
    } 

    this.update();
  }

  update() {
    const debug = false;
    if (!this.slider || !this.nextButton) {
      if(debug) { console.warn("Update called, but slider or buttons are missing."); }
      return;
    }

    if(debug) { console.log("Updating slider state..."); }

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if(debug) { console.log("Current page:", this.currentPage); }

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage !== previousPage) {
      if(debug) { console.log("Dispatching slideChanged event."); }
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    this.fitHeight();

    if (this.enableSliderLooping) return;

    this.prevButton.toggleAttribute(
      "disabled",
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
        this.slider.scrollLeft === 0
    );
    this.nextButton.toggleAttribute(
      "disabled",
      this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])
    );

    if(debug) {
      console.log("Previous button disabled:", this.prevButton.hasAttribute("disabled"));
      console.log("Next button disabled:", this.nextButton.hasAttribute("disabled"));
    }
  }

  /**
   * On phones and tablets the slides sit in a row with equal heights, so the slider was as tall as its tallest slide and
   * shorter slides left a big gap below them. Here the slider is as tall as the slide(s) currently in view and animates
   * to the new height as the visitor moves between slides. On desktop (and for the looping slideshow) it is left alone.
   */
  fitHeight() {
    if (!this.slider || this.enableSliderLooping) return;
    const items = this.sliderItemsToShow || [];
    if (!this.compactQuery || !this.compactQuery.matches || items.length < 2) {
      ['height', 'alignItems', 'overflowY', 'transition'].forEach((prop) => { this.slider.style[prop] = ''; });
      return;
    }
    // Let slides take their own height first (they are stretched to the tallest one by default), so they can be measured.
    this.slider.style.alignItems = 'flex-start';
    // The slide(s) whose box is mostly inside the visible part of the slider.
    const left = this.slider.scrollLeft;
    const right = left + this.slider.clientWidth;
    let visible = items.filter((el) => {
      const overlap = Math.min(el.offsetLeft + el.offsetWidth, right) - Math.max(el.offsetLeft, left);
      return overlap > el.offsetWidth / 2;
    });
    if (!visible.length) visible = [items[Math.min(items.length - 1, Math.max(0, (this.currentPage || 1) - 1))]];
    const height = Math.max(...visible.map((el) => el.offsetHeight));
    if (!height) return;
    const style = this.slider.style;
    style.overflowY = 'hidden';
    if (!style.transition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Only switch the transition on after the first height is set, so the page does not animate in from nothing.
      requestAnimationFrame(() => { style.transition = 'height .25s ease'; });
    }
    style.height = height + 'px';
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }


  onButtonClick(event) {
    const debug = false;
    event.preventDefault();
    if(debug) { console.log("Button clicked:", event.currentTarget.name); }

    const step = event.currentTarget.dataset.step || 1;
    if(debug) { console.log("Step:", step); }

    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;

    if(debug) { console.log("New scroll position:", this.slideScrollPosition); }
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
   const debug = false;
    if(debug) { console.log("Setting slide position:", position); }
    this.slider.scrollTo({
      left: position,
      behavior: "smooth",
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.slider || !this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout (() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);